<template>
  <div ref="list" class="results-list" :class="listModifiers" :aria-busy="loading">
    <slot></slot>
    <template v-for="({ result, tag, properties }, index) in cards" :key="result.resultGUID">
      <component
        :is="insert.element"
        v-for="insert in insertsBefore(index)"
        :key="insert.element"
        class="results-list__item"
      />
      <FinderResultCard
        class="results-list__item results-list__card"
        :tag="tag"
        :result="result"
        :properties="properties"
      />
    </template>
    <component
      :is="insert.element"
      v-for="insert in trailingInserts"
      :key="insert.element"
      class="results-list__item"
    />
  </div>
</template>

<script setup>
import { computed, useTemplateRef } from "vue";
import { useResultElements } from "#ce/components/SpFinder/composables/useResultElements";
import { useResultEvents } from "#ce/components/SpFinder/composables/useResultEvents";
import FinderResultCard from "#ce/components/SpFinder/components/FinderResultCard.ce.vue";

const props = defineProps({
  records: {
    type: Array,
    required: true,
  },
  kind: {
    type: Object,
    default: undefined,
  },
  payload: {
    type: Object,
    required: true,
  },
  settings: {
    type: Object,
    default: undefined,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  paginated: {
    type: Boolean,
    default: false,
  },
  commands: {
    type: Object,
    required: true,
  },
});

const cards = useResultElements(
  () => props.records,
  () => props.kind,
  () => ({ payload: props.payload, settings: props.settings }),
);
const listInserts = computed(() => props.kind?.listInserts ?? []);
const listModifiers = computed(() => ({ "--has-footer": props.paginated }));

// An insert sits after its count of results; a shorter list draws it at the end when the kind asks.
function insertsBefore(index) {
  return listInserts.value.filter((insert) => insert.after === index && index < cards.value.length);
}

const trailingInserts = computed(() => {
  const count = cards.value.length;
  if (count === 0) {
    return [];
  }
  return listInserts.value.filter(
    (insert) => insert.after === count || (insert.after > count && insert.whenFewer === "end"),
  );
});

useResultEvents(useTemplateRef("list"), () => props.commands);
</script>

<style lang="scss" scoped>
@use "@assets/mixins/breakpoint" as *;
@use "@ce/components/SpFinder/styles/results-container" as *;

.results-list {
  --divider: var(
    --sp-comp-finder-results-divider,
    1px solid color-mix(in srgb, var(--sp-ref-palette-black) 10.2%, var(--sp-ref-palette-white))
  );
  --footer-divider: var(--sp-comp-finder-results-footer-divider, none);

  @include finder-results-container;

  // Its cards measure this content box, so their layouts switch at the list's width.
  container: finder-result / inline-size;
  display: flex;
  flex-direction: column;

  @include sp-breakpoint(xs) {
    overflow: clip;
  }

  &__item + &__item {
    border-top: var(--divider);
  }

  &__card {
    --sp-comp-finder-result-container-name: finder-result-card;
    --item-padding-block: var(--sp-comp-finder-results-item-padding-block, var(--sp-ref-scale-48));
    --item-padding-inline: var(--sp-comp-finder-results-item-padding-inline, var(--sp-ref-scale-10));

    box-sizing: border-box;
    padding: var(--item-padding-block) var(--item-padding-inline);

    @include sp-breakpoint(sm) {
      padding: var(--sp-comp-finder-results-item-padding-block-sm, var(--item-padding-block))
        var(--sp-comp-finder-results-item-padding-inline-sm, var(--item-padding-inline));
    }
  }

  &.--has-footer::after {
    content: "";
    border-bottom: var(--footer-divider);
  }
}
</style>
